'use client';

import { AnimatePresence, motion } from 'motion/react';
import { TypedLocale } from 'payload';
import React from 'react';

import cssBreakpoints from '@/cssBreakpoints';
import { CasesSpotClient } from '@/globals/CasesSpot/ComponentClient';
import { Case, CasesSpot as CasesSpotType } from '@/payload-types';
import { cn } from '@/utils/cn';

import { CaseCard } from '../CaseCard';
import styles from './CasesList.module.scss';

type Props = {
  locale?: TypedLocale;
  cards: Case[];
  casesSpot?: CasesSpotType | false;
  filtered?: boolean;
};

const cardVariants = {
  hidden: {
    y: 50,
    opacity: 0,
  },
  visible: {
    y: 0,
    opacity: 1,
  },
};

export const CasesList: React.FC<Props> = ({
  cards,
  casesSpot = false,
  filtered = false,
  locale,
}) => {
  if (!cards || cards.length === 0) {
    return null;
  }

  let layoutIndex = 0;
  let hasRenderedSpot = false;

  return (
    <div
      className={cn(
        'container',
        { 'container--full': !filtered },
        styles.list,
        {
          [styles.filtered]: filtered,
        }
      )}
    >
      <AnimatePresence>
        {cards.map((card, index) => {
          const variantLimit = hasRenderedSpot ? 6 : 5;

          const aspectRatioCycle: Array<
            'landscape' | 'portrait' | 'square' | 'standard'
          > = [
            'standard',
            'portrait',
            'square',
            'standard',
            'portrait',
            'standard',
          ];

          const isMobile =
            filtered ||
            (typeof window !== 'undefined' &&
              window.innerWidth <= cssBreakpoints.small);

          const aspectRatio: 'landscape' | 'portrait' | 'square' | 'standard' =
            isMobile
              ? 'landscape'
              : aspectRatioCycle[layoutIndex % aspectRatioCycle.length];

          const cardClassName = cn(
            styles.card,
            {
              [styles[`card--${(layoutIndex % variantLimit) + 1}`]]: !filtered,
            }

            // index === 1 && styles[`card--2--first`]
          );

          if (!filtered && casesSpot && index === 4) {
            const element = (
              <React.Fragment key={`case-card-${card.id}`}>
                <motion.div
                  key={card.id}
                  className={cardClassName}
                  initial='hidden'
                  whileInView='visible'
                  viewport={{ once: true }}
                  variants={cardVariants}
                  style={{ willChange: 'transform, opacity' }}
                  transition={{
                    bounce: 0,
                    ease: [0.625, 0.05, 0, 1],
                    duration: 0.5,
                  }}
                  layout
                >
                  <CaseCard aspectRatio={aspectRatio} {...card} />
                </motion.div>

                <motion.div
                  key={`blog-spot`}
                  className={cn(styles.card, styles['card--spot'])}
                  initial='hidden'
                  whileInView='visible'
                  viewport={{ once: true }}
                  style={{ willChange: 'transform, opacity' }}
                  variants={cardVariants}
                  transition={{
                    bounce: 0,
                    ease: [0.625, 0.05, 0, 1],
                    duration: 0.5,
                  }}
                  layout
                >
                  <CasesSpotClient
                    casesSpot={casesSpot}
                    locale={locale || 'da'}
                  />
                </motion.div>
              </React.Fragment>
            );

            hasRenderedSpot = true;
            layoutIndex = 0;

            return element;
          }

          const element = (
            <motion.div
              key={card.id}
              className={cardClassName}
              initial='hidden'
              whileInView='visible'
              viewport={{ once: true }}
              style={{ willChange: 'transform, opacity' }}
              variants={cardVariants}
              transition={{
                bounce: 0,
                ease: [0.625, 0.05, 0, 1],
                duration: 0.5,
              }}
              layout
            >
              <CaseCard aspectRatio={aspectRatio} {...card} />
            </motion.div>
          );

          layoutIndex += 1;

          return element;
        })}
      </AnimatePresence>
    </div>
  );
};
